/**
 * Адреса страниц dnd.su: ключ записи → путь на сайте (D57).
 *
 * Отдельным файлом, а не строками в разметке, по одной причине: сайт
 * чужой. Он поменяет структуру адресов, и правка должна быть здесь,
 * а не в пяти компонентах. Отсюда же следует, что ссылка
 * необязательна: записи без адреса подсказка показывает без ссылки,
 * а не с битой.
 *
 * Ключ — ключ записи контента, то есть `srd:<ключ>` без приставки,
 * как в `terms.ts`. Второго вида ключей здесь нет: ссылаться есть
 * смысл на запись, у особенности внутри записи своей страницы
 * на dnd.su не бывает.
 *
 * ## Чего здесь нет и почему
 *
 * Разрешение администрации сайта покрывает ссылки и терминологию
 * (письмо — `docs/dnd-su-permission.md`), так что дело не в правах.
 * Дело в редакции: **на dnd.su нет материала 2024 года**, а наш
 * контент — SRD 5.2, то есть именно 2024-й. Проверено не на глаз:
 * в карте сайта 8264 адреса, и ни один не относится к PH24/XPHB;
 * в списке источников разделов «виды», «классы», «черты»
 * и «предыстории» кода этой редакции нет вовсе.
 *
 * Поэтому страница существует не у всякой нашей записи, а там, где
 * существует, она описывает редакцию 2014 года: у человека 2014-го
 * нет ни «Умелости», ни «Разносторонности». Скрывать это нельзя,
 * и подсказка не скрывает — редакция названа в самой ссылке
 * (`terms.dndSu` в словаре). Ссылка отвечает на вопрос «где про это
 * читают по-русски», а не «вот источник наших чисел»: источник —
 * SRD 5.2, и указан он в «О приложении» (`ATTRIBUTION.md`).
 *
 * Чего нет совсем:
 *
 * - **навыков** (Атлетика, Восприятие) — отдельных страниц у них
 *   на сайте нет, они перечислены внутри статей;
 * - **подклассов** — живут якорем на странице класса, а не своей
 *   страницей: ссылка вела бы не на термин, а на его родителя;
 * - **особенностей и черт вида** — страницы особенности внутри вида
 *   на сайте нет;
 * - **заклинаний, предметов, монстров** — страницы есть (525, 902
 *   и 2674), но на наши экраны ссылка на них пока не выходит:
 *   у заклинания своя вкладка с описанием. Понадобится — добавятся
 *   сюда, и это вся правка.
 *
 * Все адреса ниже проверены запросом: каждый отвечает 200.
 */

/** Сайт целиком — один раз, чтобы адреса в таблице были короткими. */
export const DND_SU_ORIGIN = 'https://dnd.su'

export const DND_SU_PAGES: Readonly<Record<string, string>> = {
  // ── Виды (раздел «Расы») ──
  dragonborn: '/race/82-dragonborn/',
  dwarf: '/race/78-dwarf/',
  elf: '/race/79-elf/',
  gnome: '/race/83-gnome/',
  goliath: '/race/103-goliath/',
  halfling: '/race/80-halfling/',
  human: '/race/81-human/',
  orc: '/race/187-orc/',
  tiefling: '/race/86-tiefling/',

  // ── Классы ──
  barbarian: '/class/87-barbarian/',
  bard: '/class/88-bard/',
  cleric: '/class/89-cleric/',
  druid: '/class/90-druid/',
  fighter: '/class/91-fighter/',
  monk: '/class/93-monk/',
  paladin: '/class/94-paladin/',
  ranger: '/class/97-ranger/',
  rogue: '/class/99-rogue/',
  sorcerer: '/class/101-sorcerer/',
  warlock: '/class/104-warlock/',
  wizard: '/class/105-wizard/',

  // ── Предыстории (на сайте — «Предыстории») ──
  acolyte: '/backgrounds/766-acolyte/',
  criminal: '/backgrounds/765-criminal/',
  sage: '/backgrounds/762-sage/',
  soldier: '/backgrounds/767-soldier/',

  // ── Черты ──
  //
  // Пять из семнадцати: прочие наши черты — дары эпического уровня
  // и боевые стили, которых в редакции 2014 года чертами не было.
  alert: '/feats/103-alert/',
  grappler: '/feats/105-grappler/',
  magic_initiate: '/feats/133-magic-initiate/',
  savage_attacker: '/feats/111-savage-attacker/',
  skilled: '/feats/130-skilled/',
}

/**
 * Адрес страницы термина или `null`, если его нет.
 *
 * Принимает и идентификатор записи (`srd:human`), и голый ключ:
 * вызывающему не приходится помнить, что именно у него в руках.
 */
export function dndSuUrl(key: string): string | null {
  const bare = key.startsWith('srd:') ? key.slice('srd:'.length) : key
  const path = DND_SU_PAGES[bare]
  return path === undefined ? null : `${DND_SU_ORIGIN}${path}`
}
